import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useUsers, useUpdateUser } from "@/hooks/use-users";
import { toast } from "sonner";

export function EditUserPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: users, isLoading } = useUsers();
  const updateUser = useUpdateUser();
  const [isEnabled, setIsEnabled] = useState(true);

  const user = users?.find((u) => u.uid === id);

  useEffect(() => {
    if (user) {
      setIsEnabled(user.isEnabled);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      await updateUser.mutateAsync({ uid: id, data: { isEnabled } });
      toast.success(`User ${id} updated`);
      navigate("/");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update user");
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-md text-center">
        <p className="text-muted-foreground">User not found.</p>
        <Button asChild variant="link">
          <Link to="/">Back to users</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-6">
      <Button variant="ghost" size="sm" asChild className="-ml-3">
        <Link to="/">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to users
        </Link>
      </Button>

      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-xl">Edit {user.uid}</CardTitle>
          <CardDescription>Enable or disable this account.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="font-medium">Account enabled</p>
                <p className="text-sm text-muted-foreground">
                  Disabled accounts cannot authenticate.
                </p>
              </div>
              <Switch
                checked={isEnabled}
                onCheckedChange={setIsEnabled}
                aria-label="Account enabled"
              />
            </div>
            <Button type="submit" className="w-full h-11" disabled={updateUser.isPending}>
              {updateUser.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
